/**
 * TradingPage
 *
 * Thin shell that renders the tabbed layout workspace (TabBar + GridCanvas)
 * with page-level chrome: SideNavigationMenu, top header with settings/order
 * ticket/"Add widget" buttons, SettingsModal, and authentication gating.
 *
 * All trading state management (orders, subscriptions, events) has been moved
 * into individual widget implementations (OrderBlotterWidget, etc.).
 *
 * Requirements: 7.1, 7.4, 7.6, 13.1
 */

import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { ClipboardList, Settings, Plus } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLayout } from '../contexts/LayoutContext';
import type { WidgetPlacement } from '../components/grid-dashboard';
import { tradingWidgetRegistry } from '../config/widgets/registry';
import { useOrderTicketNavigation } from '../features/order-ticket/navigation/OrderTicketNavigationContext';
import { SideNavigationMenu } from '../components/library/side-navigation-menu/SideNavigationMenu';
import { SettingsModal } from '../components/SettingsModal';
import { TabBar } from '../components/tab-bar/TabBar';
import { GridCanvas } from '../components/grid-canvas/GridCanvas';
import { WidgetPicker } from '../components/widget-picker/WidgetPicker';
import styles from '../components/trading/Trading.module.css';

export const TradingPage: React.FC = () => {
  const { isAuthenticated, loginWithRedirect } = useAuth();
  const { openOrderTicket } = useOrderTicketNavigation();
  const { state, dispatch } = useLayout();

  const [showSettings, setShowSettings] = useState(false);
  const [showWidgetPicker, setShowWidgetPicker] = useState(false);

  /* ── Layout wiring ───────────────────────────────────────────── */

  const activeTab = state.tabs.find((t) => t.id === state.activeTabId) ?? state.tabs[0];
  const workspaceTitle = activeTab?.name || 'Paper Trade';

  const handleSwitchTab = useCallback(
    (tabId: string) => dispatch({ type: 'SWITCH_TAB', payload: { tabId } }),
    [dispatch],
  );

  const handleCreateTab = useCallback(
    (name: string, widgets?: WidgetPlacement[]) =>
      dispatch({ type: 'CREATE_TAB', payload: { name, widgets } }),
    [dispatch],
  );

  const handleRenameTab = useCallback(
    (tabId: string, name: string) =>
      dispatch({ type: 'RENAME_TAB', payload: { tabId, name } }),
    [dispatch],
  );

  const handleCloseTab = useCallback(
    (tabId: string) => dispatch({ type: 'CLOSE_TAB', payload: { tabId } }),
    [dispatch],
  );

  const handleLayoutChange = useCallback(
    (widgets: WidgetPlacement[]) => {
      if (!activeTab) return;
      dispatch({ type: 'UPDATE_LAYOUT', payload: { tabId: activeTab.id, widgets } });
    },
    [dispatch, activeTab],
  );

  const handleRemoveWidget = useCallback(
    (instanceId: string) => {
      if (!activeTab) return;
      dispatch({ type: 'REMOVE_WIDGET', payload: { tabId: activeTab.id, instanceId } });
    },
    [dispatch, activeTab],
  );

  const handleAddWidget = useCallback(
    (widgetType: string) => {
      if (!activeTab) return;
      dispatch({ type: 'ADD_WIDGET', payload: { tabId: activeTab.id, widgetType } });
    },
    [dispatch, activeTab],
  );

  /* ── Header ──────────────────────────────────────────────────── */

  const renderTopHeader = () => (
    <header className={styles.topHeader}>
      <span className={styles.logo}>{workspaceTitle}</span>

      <div className={styles.headerActions}>
        {isAuthenticated && (
          <>
            <button
              type="button"
              className={styles.orderTicketButton}
              onClick={() => setShowWidgetPicker(true)}
              data-tooltip="Add Widget"
              aria-label="Add widget to workspace"
            >
              <Plus size={18} />
            </button>
            <button
              type="button"
              className={styles.orderTicketButton}
              onClick={() => openOrderTicket()}
              data-tooltip="Manual Order"
              aria-label="Open order ticket panel"
            >
              <ClipboardList size={18} />
            </button>
          </>
        )}
        <button
          type="button"
          className={styles.settingsButton}
          onClick={() => setShowSettings(true)}
          data-tooltip="Settings"
          aria-label="Open settings"
        >
          <Settings size={18} />
        </button>
      </div>
    </header>
  );

  /* ── Unauthenticated state ───────────────────────────────────── */

  if (!isAuthenticated) {
    return (
      <div className={styles.page}>
        <SideNavigationMenu isOpen={true} isCollapsed={true} lockCollapsed={true} />
        {renderTopHeader()}
        <div className={styles.content}>
          <section className={styles.card}>
            <header className={styles.cardHeader}>Authentication Required</header>
            <p>Please log in to access paper trading workflows.</p>
            <div className={styles.actions}>
              <button className={styles.buttonPrimary} onClick={() => loginWithRedirect()}>
                Log In
              </button>
            </div>
          </section>
        </div>
        <SettingsModal isOpen={showSettings} onClose={() => setShowSettings(false)} initialSection="general" />
      </div>
    );
  }

  /* ── Authenticated layout workspace ──────────────────────────── */

  return (
    <div className={styles.page}>
      <SideNavigationMenu isOpen={true} isCollapsed={true} lockCollapsed={true} />
      {renderTopHeader()}

      <TabBar
        tabs={state.tabs}
        activeTabId={state.activeTabId}
        onSwitchTab={handleSwitchTab}
        onCreateTab={handleCreateTab}
        onRenameTab={handleRenameTab}
        onCloseTab={handleCloseTab}
      />

      {activeTab && (
        <GridCanvas
          widgets={activeTab.widgets}
          registry={tradingWidgetRegistry}
          onLayoutChange={handleLayoutChange}
          onRemoveWidget={handleRemoveWidget}
        />
      )}

      <WidgetPicker
        isOpen={showWidgetPicker}
        onClose={() => setShowWidgetPicker(false)}
        registry={tradingWidgetRegistry}
        onAddWidget={handleAddWidget}
      />

      <SettingsModal isOpen={showSettings} onClose={() => setShowSettings(false)} initialSection="general" />
    </div>
  );
};

/* ── Error Boundary ────────────────────────────────────────────── */

interface TradingPageBoundaryState {
  hasError: boolean;
  message: string;
}

class TradingPageBoundary extends React.Component<{ children: React.ReactNode }, TradingPageBoundaryState> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, message: '' };
  }

  static getDerivedStateFromError(error: Error): TradingPageBoundaryState {
    return {
      hasError: true,
      message: error.message || 'Unexpected error while rendering paper trade page.',
    };
  }

  componentDidCatch(error: Error) {
    // eslint-disable-next-line no-console
    console.error('Paper trade page render error:', error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className={styles.page}>
          <div className={styles.content}>
            <section className={styles.card}>
              <header className={styles.cardHeader}>Paper Trade Page Error</header>
              <p>{this.state.message || 'Unable to render paper trade page.'}</p>
              <p>Try refreshing the page. If this persists, check browser console logs.</p>
              <div className={styles.actions}>
                <Link to="/home" className={styles.buttonSecondary}>Back to Home</Link>
              </div>
            </section>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const TradingPageWithBoundary: React.FC = () => (
  <TradingPageBoundary>
    <TradingPage />
  </TradingPageBoundary>
);

export default TradingPageWithBoundary;
